import '@bpd-library/components/dist/molecules/map/map';
import '@features/wishlist/components/wishlist-header/wishlist-header';
import '@features/wishlist/components/wishlist-list/wishlist-list';

import { defineElement, FC, onDidLoad, useElement } from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import { MapState, mapStore, updateMapData } from '@bpd-library/components';
import { BoundingBox } from '@bpd-library/types';
import {
    calculateBoundingBox,
    calculateMapDataBoundingBox,
    expandBoundingBox,
    extendedArea,
    ExtendedAreaModels,
    findExtendedObjectById,
    getApiObjectCenter,
    getApiObjectLayerRaster,
    useStore,
} from '@bpd-library/utilities';
import useLoadingState from '@source/components/atoms/loading-state/hooks/use-loading-state';
import MapControls from '@source/components/molecules/map-controls/map-controls';
import { getUserWishlist, userStore, wishlistLength } from '@source/store/user';
import { UserState } from '@source/store/user/utilities/store.types';
import getExtendedAreaMapData from '@source/utilities/api-utilities/get-extended-area-map-data';
import { useStylesheet, useTranslate } from '@source/utilities/hooks';
import { getDebuggerMapOptions } from '@store/debugger';
import { hasConsent, ServiceConsentState, serviceConsentStore } from '@store/service-consent';
import { elements } from '@utilities/dom';
import { handleGTMPolygonInteraction } from '@utilities/gtm-map';

import { THEME } from './utilities/config/theme';
import { extendApiModelsWithWishlist } from './utilities/provider';
import {
    filterSections,
    ListSection,
    sortSectionsByConstructionNumbers,
    sortSectionsByProjects,
    updateProjectTotals,
} from './utilities/service';
import style from './wishlist.scss';
import { ProjectTotals } from './wishlist.types';

export const Wishlist: FC = ({ element }) => {
    useStylesheet(style);

    const wishlistMap = useElement<HTMLElement>('[js-hook-wishlist-map]');
    const wishlistList = useElement<HTMLElement>('[js-hook-wishlist-list]');
    const wishlistHeader = useElement<HTMLElement>('[js-hook-wishlist-header]');

    const [, subscribe] = useStore<UserState>(userStore);
    const [, subscribeMap] = useStore<MapState>(mapStore);
    const [, subscribeConsentStore] = useStore<ServiceConsentState>(serviceConsentStore);

    const { setLoaded } = useLoadingState(element);

    let userWishlist: string[] | undefined;
    let data: ExtendedAreaModels;
    let listIsEmpty: boolean = true;
    let listSections: ListSection[] = [];
    const projectTotals: ProjectTotals = {};

    onDidLoad(async () => {
        data = await extendedArea.getData(window.__ENVIRONMENT_DETAILS__.areaId);
        data = extendApiModelsWithWishlist(data);

        handleConsent(hasConsent('mapbox'));
    });

    subscribeConsentStore(({ mapbox }) => handleConsent(mapbox), ['mapbox']);

    subscribe(
        ({ loggedIn }) => {
            if (!loggedIn) emptyWishlist();
            setupWishlist();
        },
        ['loggedIn'],
    );

    subscribe(
        ({ wishlist }) => {
            if (wishlist && wishlist.length > 0 && listIsEmpty) setupWishlist();
            updateListSections();
        },
        ['wishlist'],
    );

    subscribeMap(
        ({ hover }) => {
            if (hover.current) handleHover(hover.current?.id);
        },
        ['hover'],
    );

    subscribeMap(
        ({ click }) => {
            if (click) {
                const apiObject = findExtendedObjectById(data!, click.id);
                handleGTMPolygonInteraction(apiObject);

                if (click.url) window.open(click.url, '_self');
            }
        },
        ['click'],
    );

    function handleConsent(consent: boolean) {
        if (consent) {
            renderMap();
        } else {
            renderBanner();
        }

        setupWishlist();
        setLoaded();
    }

    async function renderMap() {
        const layerRaster = getApiObjectLayerRaster(data.areaProject);

        wishlistMap.current!.innerHTML = '';
        wishlistMap.current!.append(
            <Fragment>
                <button-toggle
                    classes="wishlist__map-expand"
                    variant="grey-90"
                    toggleOff={{
                        label: useTranslate('Mapbox-Expand'),
                        icon: 'icons-enlarge',
                    }}
                    toggleOn={{
                        label: useTranslate('Mapbox-Collapse'),
                        icon: 'icons-minimize',
                    }}
                    onClick={toggleMap}
                />

                <MapControls classes="wishlist__map-controls" sunDirection={true} />

                <div className="wishlist__mapbox-inner">
                    <bpd-map
                        options={{
                            layerRaster,
                            boundingBox: getBoundingBox(data),
                            autoOpenUrl: false,
                            ...getDebuggerMapOptions(),
                        }}
                        theme={THEME}
                    />
                </div>
            </Fragment>,
        );
    }

    function renderBanner() {
        wishlistMap.current!.innerHTML = '';
        wishlistMap.current!.append(<div uc-mapbox-banner />);
    }

    const setupWishlist = () => {
        updateMap();
        updateListSections();
        renderWishlistList();
        renderWishlistHeader();
        listIsEmpty = listSections.length === 0;
    };

    const emptyWishlist = () => {
        if (hasConsent('mapbox')) updateMapData([]);
        listSections = [];
        listIsEmpty = true;
    };

    const updateListSections = () => {
        userWishlist = getUserWishlist();

        if (userWishlist) {
            listSections = filterSections(userWishlist, data);

            sortSectionsByProjects(listSections);
            sortSectionsByConstructionNumbers(listSections);
            updateProjectTotals(listSections, projectTotals);
        }
    };

    const updateMap = () => {
        if (hasConsent('mapbox')) {
            const filteredData = filterExtendedDataByWishlist(data);
            const mapData = getExtendedAreaMapData(filteredData);

            updateMapData(mapData);
        }
    };

    const getBoundingBox = (data: ExtendedAreaModels): BoundingBox | undefined => {
        const mapData = getExtendedAreaMapData(data);

        let boundingBox: BoundingBox;

        if (mapData.length) {
            boundingBox = calculateMapDataBoundingBox(mapData);
            boundingBox = expandBoundingBox(boundingBox, 0.075);

            return boundingBox;
        } else {
            const areaProjectCenter = getApiObjectCenter(data.areaProject);

            if (areaProjectCenter) {
                boundingBox = calculateBoundingBox(areaProjectCenter);
                boundingBox = expandBoundingBox(boundingBox, 0.2);

                return boundingBox;
            }
        }

        return;
    };

    const filterExtendedDataByWishlist = (data: ExtendedAreaModels) => {
        const userWishlist = getUserWishlist() || [];

        return {
            ...data,
            constructionNumbers: data.constructionNumbers.filter(({ id }) =>
                userWishlist.includes(id),
            ),
            apartmentBuildings: data.apartmentBuildings.filter(
                ({ constructionNumberIds }) =>
                    !!constructionNumberIds?.find((id) => userWishlist.includes(id)),
            ),
        };
    };

    const renderWishlistList = () => {
        if (wishlistList.current) {
            wishlistList.current.innerHTML = '';
            wishlistList.current.append(
                <bpd-wishlist-list listData={listSections} projectTotals={projectTotals} />,
            );
        }
    };

    const renderWishlistHeader = () => {
        if (wishlistHeader.current) {
            wishlistHeader.current.innerHTML = '';
            wishlistHeader.current.append(
                <bpd-wishlist-header wishlistLength={wishlistLength()} />,
            );
        }
    };

    const toggleMap = () => {
        elements.body.classList.toggle('wishlist--map-is-expanded');
    };

    const handleHover = (id: string) => {
        if (wishlistList.current) {
            const card = wishlistList.current.querySelector(`#${id}`) as HTMLElement;

            if (card) {
                const offsetTop = card.offsetTop - wishlistList.current.offsetHeight * 0.5;

                wishlistList.current.scroll({
                    top: offsetTop,
                    left: 0,
                    behavior: 'smooth',
                });
            }
        }
    };

    return (
        <div class="c-wishlist">
            <div class="wishlist__header" js-hook-wishlist-header></div>

            <div class="wishlist__main">
                <div class="wishlist__list" js-hook-wishlist-list></div>

                <div class="wishlist__toggle-container">
                    <button-toggle
                        classes="wishlist__map-toggle"
                        variant="grey-90"
                        width="full-width"
                        toggleOff={{
                            label: useTranslate('PropertiesOverview-Map'),
                            icon: 'icons-pin',
                        }}
                        toggleOn={{
                            label: useTranslate('Mapbox-list'),
                            icon: 'icons-listview',
                        }}
                        onClick={toggleMap}
                    />
                </div>

                <div class="wishlist__mapbox" js-hook-wishlist-map />
            </div>
        </div>
    );
};

defineElement('bpd-wishlist', Wishlist);
